import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/cn';
import { isSupabaseConfigured } from '../lib/supabase';
import { useT } from '../lib/i18n';

export const LoginPage: React.FC = () => {
  const { signIn, signUp, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  // Already logged in
  if (isAuthenticated) {
    navigate('/brain-dump', { replace: true });
    return null;
  }

  // Supabase not configured — skip login
  if (!isSupabaseConfigured()) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!email.trim() || !password.trim()) {
      setError(t('loginPage.enterEmailAndPassword'));
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t('loginPage.passwordTooShort'));
      setLoading(false);
      return;
    }

    const result = mode === 'login'
      ? await signIn(email, password)
      : await signUp(email, password);

    if (result.error) {
      setError(result.error);
    } else if (mode === 'signup') {
      setSignupSuccess(true);
    } else {
      navigate('/brain-dump', { replace: true });
    }

    setLoading(false);
  };

  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-success/10 border border-success/30 flex items-center justify-center mx-auto">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-success">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-text-primary">{t('loginPage.confirmationSent')}</h2>
          <p className="text-sm text-text-muted">
            {t('loginPage.confirmationDesc', { email })}
          </p>
          <button
            onClick={() => { setSignupSuccess(false); setMode('login'); }}
            className="text-brand text-sm font-medium hover:underline bg-transparent border-none cursor-pointer"
          >
            {t('loginPage.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="max-w-sm w-full">
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text-primary tracking-tight mb-1">Bangerfy</h1>
          <p className="text-sm text-text-muted">{t('loginPage.appDescription')}</p>
        </div>

        {/* Email Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={t('loginPage.email')}
            className="w-full py-3 px-4 bg-surface-2 border border-border rounded-xl text-text-primary text-[13px] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 transition-all"
            autoComplete="email"
          />
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('loginPage.password')}
            className="w-full py-3 px-4 bg-surface-2 border border-border rounded-xl text-text-primary text-[13px] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 transition-all"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />

          {error && <p className="text-danger text-[12px] m-0">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className={cn(
              'w-full py-3 rounded-xl text-[14px] font-semibold border-none cursor-pointer transition-all duration-200',
              loading ? 'bg-surface-3 text-text-faint cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-hover'
            )}
          >
            {loading ? t('common.processing') : mode === 'login' ? t('loginPage.loginButton') : t('loginPage.signupButton')}
          </button>
        </form>

        {/* Toggle */}
        <p className="text-center text-[12px] text-text-muted mt-4">
          {mode === 'login' ? t('loginPage.noAccount') : t('loginPage.hasAccount')}
          <button
            onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(null); }}
            className="text-brand font-medium ml-1 bg-transparent border-none cursor-pointer hover:underline"
          >
            {mode === 'login' ? t('loginPage.signUp') : t('loginPage.logIn')}
          </button>
        </p>
      </div>
    </div>
  );
};
